import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const WIDTH = 1080;
const HEIGHT = 1080;

function GraphicPlayerSpotlightCard({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  canUseCutouts = false,
}) {
  const {
    highlightPlayer,
    highlightStats,
    homeTeam,
    backgroundPhoto,
    dominantImage,
    dominantImageX,
    dominantImageY,
    dominantImageScale,
    spotlightLabelFont,
    spotlightLabelSize,
    spotlightLabelColor,
  } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const scale = (dominantImageScale ?? 100) / 100;
  const heroFamily = "'Bebas Neue', 'Arial Narrow', Arial, sans-serif";
  const barlowFamily = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const pillFont = spotlightLabelFont
    ? `'${spotlightLabelFont}', 'Arial Narrow', Arial, sans-serif`
    : barlowFamily;
  const pillSize = spotlightLabelSize ?? 32;
  const pillColor = spotlightLabelColor || '#ffffff';
  const pillX = graphicData.spotlightLabelX ?? 900;

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 50%, ${teamColors.secondary} 50%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const statsLines = highlightStats ? highlightStats.split('\n').filter(Boolean) : [];
  const statsDisplay = statsLines.length > 0 ? statsLines.join(' · ') : 'STATS & ACHIEVEMENTS';

  return (
    <div
      id="graphic-player-spotlight-card"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFamily,
      }}
    >
      {/* Layer 1: Top half background photo (clipped to top 600px) */}
      {backgroundPhoto && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: WIDTH,
            height: 600,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* Layer 2: Top half dark overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: 600,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2,
        }}
      />

      {/* Layer 3: Dominant image */}
      {dominantImage && (
        <img
          src={dominantImage}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: canUseCutouts ? `${dominantImageY ?? 50}%` : 300,
            left: canUseCutouts ? `${dominantImageX ?? 50}%` : 540,
            transform: canUseCutouts
              ? `translate(-50%, -50%) scale(${scale})`
              : 'translate(-50%, -50%) scale(1)',
            transformOrigin: 'center center',
            width: canUseCutouts ? '100%' : 'auto',
            height: 'auto',
            maxHeight: canUseCutouts ? 'none' : 560,
            maxWidth: 'none',
            overflow: 'visible',
            zIndex: 3,
          }}
        />
      )}

      {/* Layer 4: Bottom info block */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: WIDTH,
          height: 480,
          background: teamColors.primary,
          zIndex: 4,
        }}
      />

      {/* Layer 5: Player name */}
      <div
        style={{
          position: 'absolute',
          bottom: 280,
          left: 0,
          width: WIDTH,
          textAlign: 'center',
          color: '#ffffff',
          fontFamily: heroFamily,
          fontSize: 110,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 2,
          lineHeight: 1,
          zIndex: 5,
        }}
      >
        {highlightPlayer || 'PLAYER NAME'}
      </div>

      {/* Layer 6: Stats */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 0,
          width: WIDTH,
          textAlign: 'center',
          color: '#ffffff',
          fontFamily: barlowFamily,
          fontSize: 38,
          opacity: 0.9,
          letterSpacing: 1,
          paddingLeft: 48,
          paddingRight: 48,
          zIndex: 6,
        }}
      >
        {statsDisplay}
      </div>

      {/* Layer 7: Team logo (top-left) */}
      {homeTeamLogo && (
        <img
          src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
          alt={homeTeam || 'Team'}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 32,
            left: 48,
            height: 120,
            width: 'auto',
            zIndex: 7,
          }}
        />
      )}

      {/* Layer 8: PLAYER SPOTLIGHT label (top-right) */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: pillX,
          transform: 'translateX(-50%)',
          background: teamColors.secondary,
          color: pillColor,
          fontFamily: pillFont,
          fontSize: pillSize,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 2,
          whiteSpace: 'nowrap',
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 28,
          paddingRight: 28,
          borderRadius: 8,
          lineHeight: 1,
          zIndex: 8,
        }}
      >
        PLAYER SPOTLIGHT
      </div>

      {/* Layer 9: Divider between top and bottom halves */}
      <div
        style={{
          position: 'absolute',
          top: 600,
          left: 0,
          width: WIDTH,
          height: 8,
          background: teamColors.secondary,
          zIndex: 9,
        }}
      />

      {/* Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <img
            src="/PMlogoleft.png"
            alt="Playmaker Media Kit"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}

export default GraphicPlayerSpotlightCard;
